import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { G } from "@/brand";
import api from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { FeedbackModal } from "@/components/FeedbackModal";
import { SavedTracks } from "@/components/SavedTracks";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, BookOpen, Wand2, FileText, MessageSquarePlus, Gift, Copy, Check, Twitter, Mail } from "lucide-react";

const TIER_LABEL = { free: "Prompt Savant (Free)", upgrade_1: "Vault — Tier 1", upgrade_2: "Codex — Tier 2" };

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [hist, setHist] = useState({ opens: [], edits: [], ledger: [] });
  const [fb, setFb] = useState(false);
  const [ref, setRef] = useState({ referral_id: "", count: 0 });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user === false) { nav("/"); return; }
    if (user?.email) {
      api.get("/me/history").then(({ data }) => setHist(data));
      api.get("/me/referrals").then(({ data }) => setRef(data)).catch(() => {});
    }
  }, [user, nav]);

  const refLink = ref.referral_id ? `${window.location.origin}/?ref=${ref.referral_id}` : "";
  const shareMsg = "I've been using Prompt Savant for AI music prompts — 50 free to start, built for Suno & Udio. Grab yours:";
  const copyLink = () => { navigator.clipboard.writeText(refLink).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 1600); };

  if (!user?.email) return <div style={{ background: G.black, minHeight: "100vh" }}><Nav /></div>;

  const stats = [
    { icon: Sparkles, label: "Credit Balance", value: user.credit_balance, accent: G.gold },
    { icon: BookOpen, label: "Prompts Opened", value: user.prompts_opened_count, accent: G.gold },
    { icon: Wand2, label: "AI Edits Used", value: user.edits_used_count, accent: G.orange },
  ];

  return (
    <div style={{ background: G.black, minHeight: "100vh" }}>
      <Nav />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "130px 5vw 80px" }}>
        <div className="mono" style={{ fontSize: 9, letterSpacing: "0.5em", color: G.gold, marginBottom: 12 }}>ACCOUNT</div>
        <h1 style={{ fontSize: "clamp(28px,4vw,46px)", fontWeight: 300, fontStyle: "italic", color: "#fff" }}>{user.email}</h1>
        <div className="mono" data-testid="dash-tier" style={{ fontSize: 11, letterSpacing: ".15em", color: G.gold, marginTop: 8 }}>
          ACCESS: {TIER_LABEL[user.access_tier]?.toUpperCase()}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 18, marginTop: 36 }}>
          {stats.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label}`} style={{ background: G.dark, border: `1px solid ${G.border}`, padding: 24 }}>
              <s.icon size={20} color={s.accent} />
              <div style={{ fontSize: 38, fontWeight: 700, color: s.accent, marginTop: 12 }}>{s.value}</div>
              <div className="mono" style={{ fontSize: 9, letterSpacing: ".15em", color: G.goldDim, marginTop: 4 }}>{s.label.toUpperCase()}</div>
            </div>
          ))}
        </div>

        {user.access_tier === "free" && (
          <div style={{ marginTop: 28, border: `1px solid ${G.orange}`, padding: 24, background: G.dark, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ fontSize: 18, color: "#fff", fontStyle: "italic" }}>Unlock premium prompts + AI editing.</div>
            <button onClick={() => nav("/pricing")} className="btn-orange" style={{ padding: "12px 26px", fontSize: 10 }}>Upgrade Now</button>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 22, marginTop: 40 }}>
          <Panel title="OPENED PROMPTS" empty="No prompts opened yet." rows={hist.opens.map((o) => ({ k: o.id, a: `${o.genre_name} · ${o.category}`, b: new Date(o.opened_at).toLocaleDateString() }))} />
          <Panel title="AI EDIT HISTORY" icon={Wand2} empty="No AI edits yet." rows={hist.edits.map((e) => ({ k: e.id, a: e.instruction, b: `−${e.cost} CR` }))} />
        </div>

        <div style={{ marginTop: 22 }}>
          <Panel title="CREDIT LEDGER" icon={FileText} empty="No activity yet."
            rows={hist.ledger.map((l) => ({ k: l.id, a: `${l.kind.toUpperCase()} · ${new Date(l.created_at).toLocaleString()}`, b: `${l.amount > 0 ? "+" : ""}${l.amount} → ${l.balance_after}` }))} />
        </div>

        <SavedTracks />

        {/* REFER A FRIEND */}
        <div data-testid="referral-card" style={{ marginTop: 40, border: `1px solid ${G.border}`, background: "#16130a", padding: 28, position: "relative", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <Gift size={20} color={G.gold} />
            <div className="mono" style={{ fontSize: 9, letterSpacing: ".3em", color: G.gold }}>REFER A FRIEND</div>
          </div>
          <div style={{ fontSize: 22, color: "#fff", fontStyle: "italic", marginBottom: 6 }}>Share the codex. Grow the sound.</div>
          <div style={{ color: G.muted, fontStyle: "italic", fontSize: 14.5, marginBottom: 18, maxWidth: 620, lineHeight: 1.6 }}>
            Send friends your personal link — they land on Prompt Savant ready to grab 50 free prompts and join the drop list.
            {ref.count > 0 && <span style={{ color: G.gold }}> {ref.count} {ref.count === 1 ? "friend has" : "friends have"} joined via your link.</span>}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input data-testid="referral-link" readOnly value={refLink} onFocus={(e) => e.target.select()}
              style={{ flex: "1 1 320px", minWidth: 0, padding: "12px 14px", background: G.black, border: `1px solid ${G.border}`, color: G.text, fontSize: 13, fontFamily: "monospace" }} />
            <button data-testid="referral-copy" onClick={copyLink} className="btn-gold" style={{ padding: "12px 20px", fontSize: 10, display: "inline-flex", alignItems: "center", gap: 8 }}>
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy Link"}
            </button>
            <a data-testid="referral-x" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMsg)}&url=${encodeURIComponent(refLink)}`} target="_blank" rel="noreferrer"
              className="btn-outline" style={{ padding: "12px 16px", fontSize: 10, display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
              <Twitter size={13} /> Share on X
            </a>
            <a data-testid="referral-email" href={`mailto:?subject=${encodeURIComponent("You should try Prompt Savant")}&body=${encodeURIComponent(shareMsg + " " + refLink)}`}
              className="btn-outline" style={{ padding: "12px 16px", fontSize: 10, display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
              <Mail size={13} /> Email
            </a>
          </div>
        </div>
      </div>

      <button data-testid="open-feedback" onClick={() => setFb(true)} className="btn-gold" style={{ position: "fixed", bottom: 24, right: 24, zIndex: 300, padding: "12px 18px", fontSize: 10, display: "flex", alignItems: "center", gap: 8 }}>
        <MessageSquarePlus size={14} /> Suggest
      </button>
      <FeedbackModal open={fb} onClose={() => setFb(false)} />
      <Footer />
    </div>
  );
}

function Panel({ title, rows, empty, icon: Icon }) {
  return (
    <div style={{ background: G.dark, border: `1px solid ${G.border}`, padding: 22 }}>
      <div className="mono" style={{ fontSize: 9, letterSpacing: ".3em", color: G.gold, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
        {Icon && <Icon size={12} />} {title}
      </div>
      {rows.length === 0 ? <div style={{ color: G.muted, fontStyle: "italic", fontSize: 14 }}>{empty}</div> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 260, overflowY: "auto" }}>
          {rows.map((r) => (
            <div key={r.k} style={{ display: "flex", justifyContent: "space-between", gap: 12, borderBottom: `1px solid ${G.border}33`, paddingBottom: 8 }}>
              <span style={{ fontSize: 13.5, color: G.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.a}</span>
              <span className="mono" style={{ fontSize: 10, color: G.muted, flexShrink: 0 }}>{r.b}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
